import * as React from "react";

import { DataGrid } from "@mui/x-data-grid";
import ResultsChart from "./ResultsChart";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

class Result extends React.Component {
  constructor(props) {
    super(props);

    this.formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });

    this.columns = [
      {
        field: "age",
        headerName: "Age",
        type: "number",
        width: 75,
      },
      {
        field: "schdShares",
        headerName: "SCHD Shares",
        type: "number",
        width: 100,
        valueGetter: (params) => `${Math.round(params.row.schdShares)}`,
      },
      {
        field: "mgkShares",
        headerName: "MGK Shares",
        type: "number",
        width: 100,
        valueGetter: (params) => `${Math.round(params.row.mgkShares)}`,
      },
      {
        field: "schdInvestmentReturn",
        headerName: "SCHD Investment Return %",
        type: "number",
        width: 100,
        valueGetter: (params) =>
          `${Math.round(params.row.schdInvestmentReturn)}%`,
      },
      {
        field: "mgkInvestmentReturn",
        headerName: "MGK Investment Return %",
        type: "number",
        width: 100,
        valueGetter: (params) =>
          `${Math.round(params.row.mgkInvestmentReturn)}%`,
      },
      {
        field: "schdInvestmentReturns",
        headerName: "SCHD Investment Return $",
        type: "number",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.schdInvestmentReturns)}`,
      },
      {
        field: "mgkInvestmentReturns",
        headerName: "MGK Investment Return $",
        type: "number",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.mgkInvestmentReturns)}`,
      },
      {
        field: "schdDividendPerShare",
        headerName: "SCHD Dividend per share $",
        type: "number",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.schdDividendPerShare)}`,
      },
      {
        field: "mgkDividendPerShare",
        headerName: "MGK Dividend per share $",
        type: "number",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.mgkDividendPerShare)}`,
      },
      {
        field: "dividends",
        headerName: "Dividends $",
        type: "number",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.dividends)}`,
      },
      {
        field: "netIncome",
        headerName: "Net Income $",
        type: "number",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.netIncome)}`,
      },
      {
        field: "investmentWithdraw",
        headerName: "Investment Withdraw",
        type: "number",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.investmentWithdraw)}`,
      },
      {
        field: "taxes",
        headerName: "Taxes",
        type: "number",
        flex: 1,
        valueGetter: (params) => `${this.formatter.format(params.row.taxes)}`,
      },
      {
        field: "yearlyExpenses",
        headerName: "Expenses",
        type: "number",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.yearlyExpenses)}`,
      },
      {
        field: "incomeOverExpenses",
        headerName: "Income Over Expenses",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.incomeOverExpenses)}`,
      },
      {
        field: "investmentBalance",
        headerName: "Investment Balance",
        flex: 1,
        valueGetter: (params) =>
          `${this.formatter.format(params.row.investmentBalance)}`,
      },
    ];

    this.state = {
      chartTab: 0,
      data: props.data,
      index: props.index,
    };
  }

  render() {
    return (
      <div
        key={this.props.data.trial}
        style={{ width: "100%", marginTop: this.props.index === 0 ? 0 : 150 }}
      >
        <h1>
          Trial {this.props.data.trial + 1} - Avg Return: &nbsp;
          {Math.round(this.props.data.avgReturn * 100)}%
          <br />
          Retirement Balance
          <br />
          {this.formatter.format(this.props.data.retirementBalance)}
        </h1>

        <div className="container mt-5" style={{ height: 375, marginTop: 50 }}>
          <DataGrid
            rows={this.props.data.results}
            columns={this.columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
          />
        </div>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={this.state.chartTab}
            onChange={this.tabChange.bind(this)}
          >
            <Tab label="Investment Balance" {...a11yProps(0)} />
            <Tab label="Dividends / Expenses" {...a11yProps(1)} />
            <Tab label="SCHD" {...a11yProps(2)} />
            <Tab label="SCHD Dividend Per Share" {...a11yProps(3)} />
            <Tab label="MGK" {...a11yProps(4)} />
            <Tab label="MGK Dividend Per Share" {...a11yProps(5)} />
          </Tabs>

          <TabPanel value={this.state.chartTab} index={0}>
            <ResultsChart
              hAxisTitle="Years"
              vAxisTitle="Amount"
              chartData={this.buildChartData(
                this.props.data,
                "Investment Balance",
                "investmentBalance"
              )}
            />
          </TabPanel>
          <TabPanel value={this.state.chartTab} index={1}>
            <ResultsChart
              hAxisTitle="Years"
              vAxisTitle="Amount"
              chartData={this.buildDividendChartData(this.props.data)}
            />
          </TabPanel>
          <TabPanel value={this.state.chartTab} index={2}>
            <ResultsChart
              hAxisTitle="Years"
              vAxisTitle="Price"
              chartData={this.buildChartData(
                this.props.data,
                "SCHD",
                "schdPrice"
              )}
            />
          </TabPanel>
          <TabPanel value={this.state.chartTab} index={3}>
            <ResultsChart
              hAxisTitle="Years"
              vAxisTitle="Price"
              chartData={this.buildChartData(
                this.props.data,
                "SCHD Price Per Share",
                "schdDividendPerShare"
              )}
            />
          </TabPanel>
          <TabPanel value={this.state.chartTab} index={4}>
            <ResultsChart
              hAxisTitle="Years"
              vAxisTitle="Price"
              chartData={this.buildChartData(
                this.props.data,
                "MGK",
                "mgkPrice"
              )}
            />
          </TabPanel>

          <TabPanel value={this.state.chartTab} index={5}>
            <ResultsChart
              hAxisTitle="Years"
              vAxisTitle="Price"
              chartData={this.buildChartData(
                this.props.data,
                "MGK Price Per Share",
                "mgkDividendPerShare"
              )}
            />
          </TabPanel>
        </Box>
      </div>
    );
  }

  tabChange(event, newValue) {
    this.setState({ chartTab: newValue });
  }

  buildChartData(data, title, key) {
    let counter = 1;
    let chartData = data.results.map((item) => {
      let data = [counter, item[key]];

      counter++;

      return data;
    });

    chartData.unshift(["x", title]);

    return chartData;
  }

  buildDividendChartData(data) {
    let counter = 1;
    let chartData = data.results.map((item) => {
      let data = [counter, item.yearlyExpenses, item.dividends];

      counter++;

      return data;
    });

    chartData.unshift(["x", "expenses", "dividends"]);

    return chartData;
  }
}

export default Result;
